import { useEffect, useRef, useState } from 'react';
import { WEBSITE_CLIENT_CONTRACT, WEBSITE_RUNTIME_PATH } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { useNavBlocker, useNavBlockerMarkSettled } from '@/hooks/use-nav-blocker';
import { useUnsavedEditGuard } from '../hooks/use-unsaved-edit-guard';
import { useWebsiteT } from '../hooks/use-website-t';
import { isTemplateEditPath, TEMPLATE_LIBRARY_PATH } from '../lib/template-routes';
import { UnsavedChangesDialog } from './unsaved-changes-dialog';

export interface WebsiteRuntimeNoticeProps {
  readonly storeId?: string | null;
  readonly isDirty?: boolean;
  /** The region dialog can supply its already-mounted unsaved-work question. */
  readonly guardReload?: (action: () => void) => void;
  readonly loadRuntime?: () => Promise<unknown>;
  readonly navigateDocument?: (path: string) => void;
  readonly storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

async function loadCurrentRuntime(): Promise<unknown> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch(WEBSITE_RUNTIME_PATH, {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error('runtime_unavailable');
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
  }
}

function supportedBuildId(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  if (keys.length !== 2 || keys[0] !== 'buildId' || keys[1] !== 'clientContracts') return null;
  if (typeof record.buildId !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(record.buildId))
    return null;
  if (
    !Array.isArray(record.clientContracts) ||
    record.clientContracts.length !== 1 ||
    record.clientContracts[0] !== WEBSITE_CLIENT_CONTRACT
  )
    return null;
  return record.buildId;
}

function RetainedDirtyGuard({ isDirty }: { readonly isDirty: boolean }): null {
  useNavBlocker(isDirty);
  return null;
}

/** Explicit, bounded document recovery. A contract declaration never grants store access. */
export function WebsiteRuntimeNotice({
  storeId = null,
  isDirty = false,
  guardReload,
  loadRuntime = loadCurrentRuntime,
  navigateDocument = (path) => window.location.assign(path),
  storage,
}: WebsiteRuntimeNoticeProps): React.JSX.Element {
  const { t, dir } = useWebsiteT();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [failedGuardEpoch, setFailedGuardEpoch] = useState(0);
  const attemptedInDocument = useRef(new Set<string>());
  const inFlight = useRef(false);
  const pageHideListener = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      if (pageHideListener.current)
        window.removeEventListener('pagehide', pageHideListener.current);
    },
    [],
  );
  const markSettled = useNavBlockerMarkSettled();
  const guard = useUnsavedEditGuard({ isDirty, onClose: () => undefined });

  async function reload(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setFailed(false);
    let persistAttempt: (() => void) | null = null;
    let attemptKey: string | null = null;
    try {
      const buildId = supportedBuildId(await loadRuntime());
      if (!buildId) throw new Error('runtime_unavailable');
      // Preserve only known Admin routes, never arbitrary query strings, fragments or URLs:
      // the library, or exactly one template's edit route (epic #228 P3).
      const pathname = window.location.pathname;
      const path =
        pathname === TEMPLATE_LIBRARY_PATH || isTemplateEditPath(pathname)
          ? pathname
          : '/storefront';
      const attempts = storage ?? window.sessionStorage;
      const key = `dukanti:website-runtime:admin:${storeId ?? 'unresolved'}:${path}:${WEBSITE_CLIENT_CONTRACT}:${buildId}`;
      if (attempts.getItem(key) !== null || attemptedInDocument.current.has(key)) {
        throw new Error('runtime_already_attempted');
      }
      // Fail before navigation when storage is unavailable. The probe is not a reload attempt.
      attempts.setItem(`${key}:probe`, 'available');
      attempts.removeItem(`${key}:probe`);
      attemptKey = key;
      attemptedInDocument.current.add(key);
      persistAttempt = (): void => {
        attempts.setItem(key, 'attempted');
      };
      pageHideListener.current = persistAttempt;
      window.addEventListener('pagehide', persistAttempt, { once: true });
      // The explicit discard decision has already run; avoid a second native unload prompt.
      markSettled();
      navigateDocument(path);
    } catch {
      if (persistAttempt) window.removeEventListener('pagehide', persistAttempt);
      pageHideListener.current = null;
      if (attemptKey) attemptedInDocument.current.delete(attemptKey);
      setFailedGuardEpoch((epoch) => epoch + 1);
      setFailed(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  return (
    <>
      {/* Remount after each failed navigation to restore the existing scope registry reliably. */}
      <RetainedDirtyGuard key={failedGuardEpoch} isDirty={isDirty && failedGuardEpoch > 0} />
      <section
        dir={dir}
        role="alert"
        className="border-border bg-admin-surface-muted space-y-3 rounded-xl border p-4 text-start text-sm"
        data-testid="website-runtime-notice"
      >
        <p className="text-foreground">{t('admin.websiteTemplates.errors.templateUnavailable')}</p>
        {isDirty ? (
          <p className="text-muted-foreground">
            {t('admin.websiteTemplates.runtime.unsavedWarning')}
          </p>
        ) : null}
        {failed ? (
          <p className="text-destructive" data-testid="website-runtime-reload-error">
            {t('admin.websiteTemplates.runtime.reloadFailed')}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="min-h-11 max-w-full whitespace-normal"
            disabled={pending}
            data-testid="website-runtime-reload"
            onClick={() => {
              const request = (): void => {
                void reload();
              };
              if (guardReload) guardReload(request);
              else guard.guard(request);
            }}
          >
            {t(
              pending
                ? 'admin.websiteTemplates.frame.loading'
                : 'admin.websiteTemplates.runtime.reload',
            )}
          </Button>
          <a
            href="/dashboard"
            className="text-foreground inline-flex min-h-11 items-center rounded px-3 underline focus-visible:outline focus-visible:outline-2"
            data-testid="website-runtime-dashboard"
          >
            {t('admin.nav.dashboard')}
          </a>
        </div>
      </section>
      {guardReload ? null : (
        <UnsavedChangesDialog
          open={guard.confirmOpen}
          onKeepEditing={guard.keepEditing}
          onDiscard={guard.discard}
        />
      )}
    </>
  );
}
